import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useState, useRef, useEffect } from 'react';
import { shape, string, arrayOf, bool, func, number, element, oneOfType } from 'prop-types';
import { Icon, FormItem, FormInputDropdown, FormInputText } from '@tesla/design-system-react';
import { iconLoader, iconClose, iconSearch } from '@tesla/design-system-icons';
import classnames from 'classnames';
import { parseJwt, isChina } from './Helpers/Utilities.js';
import { urls } from './Helpers/Const.js';
import { checkIsVin } from '../../utils/checkRegex/index.js';
import SearchResultsList from './Components/SearchResultsList.js';
import useSuggestions from './Hooks/useSuggestions.js';
import useScopes from './Hooks/useScopes.js';
import useKeyPress from './Hooks/useKeyPress.js';
import styles from './styles.module.css.js';

var SearchBar = function SearchBar(props) {
  var _scopeList$;
  var embedded = props.embedded,
    placeholder = props.placeholder,
    searchCount = props.searchCount,
    adfsToken = props.adfsToken,
    recent = props.recent,
    aosUrl = props.aosUrl,
    appName = props.appName,
    className = props.className,
    listData = props.listData,
    onSelect = props.onSelect,
    type = props.type,
    accountId = props.accountId,
    historyData = props.historyData,
    onChange = props.onChange,
    value = props.value,
    id = props.id,
    autoFocus = props.autoFocus,
    trailingComponent = props.trailingComponent,
    filters = props.filters,
    CustomDDLOption = props.CustomDDLOption,
    scopeList = props.scopeList,
    enableScope = props.enableScope,
    locale = props.locale,
    showSearchIconLeft = props.showSearchIconLeft,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    contactSearch = props.contactSearch,
    dense = props.dense,
    hideScopeDropdown = props.hideScopeDropdown,
    customSearchFunc = props.customSearchFunc,
    customTransformFunc = props.customTransformFunc,
    debounceTime = props.debounceTime,
    C360SearchTestConsumerApp = props.C360SearchTestConsumerApp;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    ignoreHover = _useState2[0],
    setIgHover = _useState2[1];
  var _useState3 = useState(autoFocus),
    _useState4 = _slicedToArray(_useState3, 2),
    isFocus = _useState4[0],
    setFocus = _useState4[1];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    suggest = _useState6[0],
    setSuggest = _useState6[1];
  var _useState7 = useState({}),
    _useState8 = _slicedToArray(_useState7, 2),
    cursor = _useState8[0],
    setCursor = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    loading = _useState10[0],
    setLoading = _useState10[1];
  var urlParams = new URLSearchParams(window.location.search);
  var _useState11 = useState(urlParams.get('scope') || (scopeList === null || scopeList === void 0 ? void 0 : (_scopeList$ = scopeList[0]) === null || _scopeList$ === void 0 ? void 0 : _scopeList$.value) || 'all'),
    _useState12 = _slicedToArray(_useState11, 2),
    scope = _useState12[0],
    setScope = _useState12[1];
  var enterPress = useKeyPress('Enter', isFocus);
  var searchBarRef = useRef(null);
  var userInfo = parseJwt(adfsToken);
  var userName = userInfo === null || userInfo === void 0 ? void 0 : userInfo.samaccountname;
  var finalScopeList = [];
  if (enableScope) {
    var useUserScope = Array.isArray(scopeList) && scopeList.length;
    finalScopeList = useUserScope ? scopeList : useScopes({
      aosUrl: aosUrl,
      adfsToken: adfsToken,
      locale: locale
    });
  }
  var _useSuggestions = useSuggestions({
      isFocus: isFocus,
      value: value,
      onChange: onChange,
      suggest: suggest,
      recent: recent,
      searchCount: searchCount,
      adfsToken: adfsToken,
      aosUrl: aosUrl,
      appName: appName,
      listData: listData,
      onSelect: onSelect,
      type: type,
      accountId: accountId,
      historyData: historyData,
      id: id,
      filters: filters,
      setLoading: setLoading,
      scope: scope,
      locale: locale,
      customSearchFunc: customSearchFunc,
      customTransformFunc: customTransformFunc,
      debounceTime: debounceTime,
      C360SearchTestConsumerApp: C360SearchTestConsumerApp
    }),
    searchResults = _useSuggestions.searchResults,
    isHistory = _useSuggestions.isHistory;
  useEffect(function () {
    if (searchResults !== null && searchResults !== void 0 && searchResults.length) setSuggest(true);
  }, [searchResults]);
  useEffect(function () {
    if (isFocus) setCursor({});
  }, [isFocus, value]);
  var clearSearch = function clearSearch() {
    var _searchBarRef$current;
    searchBarRef === null || searchBarRef === void 0 ? void 0 : (_searchBarRef$current = searchBarRef.current) === null || _searchBarRef$current === void 0 ? void 0 : _searchBarRef$current.focus();
    setFocus(true);
    setSuggest(true);
    // true signifies that the cross icon has been clicked
    onChange('', true);
  };
  var navigateToAOS = function navigateToAOS(opt, searchTerm, url, selectedScope) {
    var localePrefix = locale ? "/".concat(locale) : '';
    var navigateTo;
    if (opt !== null && opt !== void 0 && opt.account_id) {
      if (opt !== null && opt !== void 0 && opt.matchedValue) {
        navigateTo = "".concat(localePrefix).concat(urls.vehicleDetails, "/").concat(opt === null || opt === void 0 ? void 0 : opt.matchedValue, "/assetroles");
      } else {
        navigateTo = "".concat(localePrefix).concat(url, "/accountid/").concat(encodeURIComponent(opt.account_id));
      }
    } else {
      navigateTo = "".concat(localePrefix).concat(url, "/query/").concat(encodeURIComponent(searchTerm), "?scope=").concat(encodeURIComponent(selectedScope));
    }
    window.location.href = navigateTo;
  };
  var updateSearchHistory = function updateSearchHistory(searchTerm) {
    var body = JSON.stringify({
      searchTerm: searchTerm,
      applicationName: appName,
      userName: userName,
      accountId: accountId,
      type: type
    });
    var headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: adfsToken,
      'x-account-locale': locale
    };
    var options = {
      method: 'POST',
      headers: headers,
      body: body,
      credentials: 'omit'
    };
    var endpoint = aosUrl + urls.recentSearchUrl;
    fetch(endpoint, options)["catch"](function () {});
    setSuggest(false);
    setCursor({});
  };
  var triggerC360Search = function triggerC360Search(opt) {
    if (contactSearch) {
      return;
    }
    if (opt !== null && opt !== void 0 && opt.label || value.length) {
      if (opt !== null && opt !== void 0 && opt.label && onChange) onChange(opt.label);
      var searchTerm = (opt === null || opt === void 0 ? void 0 : opt.label) || value;
      // don't update search history if feature is switched off
      if (historyData !== null) {
        updateSearchHistory(searchTerm);
      }
      setTimeout(function () {
        if (onSelect) onSelect(opt, searchTerm);else if (checkIsVin(searchTerm)) {
          var localePrefix = locale ? "/".concat(locale) : '';
          var navigateTo = "".concat(localePrefix).concat(urls.vehicleDetails, "/").concat(searchTerm, "/assetroles");
          window.location.href = navigateTo;
        } else navigateToAOS(opt, searchTerm, urls.searchResultsUrl, scope);
      }, 200);
    }
  };
  useEffect(function () {
    if (isFocus && enterPress && !suggest) {
      var _Object$keys, _searchResults$cursor, _searchResults$cursor2;
      if ((_Object$keys = Object.keys(cursor)) !== null && _Object$keys !== void 0 && _Object$keys.length) triggerC360Search(searchResults === null || searchResults === void 0 ? void 0 : (_searchResults$cursor = searchResults[cursor === null || cursor === void 0 ? void 0 : cursor.type]) === null || _searchResults$cursor === void 0 ? void 0 : (_searchResults$cursor2 = _searchResults$cursor.data) === null || _searchResults$cursor2 === void 0 ? void 0 : _searchResults$cursor2[cursor === null || cursor === void 0 ? void 0 : cursor.item]);else triggerC360Search();
    }
  }, [enterPress]);
  var loaderIcon = /*#__PURE__*/React.createElement(Icon, {
    data: iconLoader,
    className: classnames('tds-icon--small', styles['search-bar--loader'])
  });
  var closeIcon = /*#__PURE__*/React.createElement(Icon, {
    data: iconClose,
    onClick: clearSearch,
    className: classnames(styles.pointer, 'tds-icon--small')
  });
  var searchIcon = /*#__PURE__*/React.createElement(Icon, {
    data: iconSearch,
    inline: true,
    key: "searchIcon-".concat(id),
    onClick: triggerC360Search,
    className: classnames(styles.pointer, value ? null : 'tds-text_color--40')
  });
  var clearIconForSearchBar = value.length ? closeIcon : null;
  var loader = loading ? loaderIcon : clearIconForSearchBar;
  var trailingIcon = showSearchIconLeft ? /*#__PURE__*/React.createElement(React.Fragment, null, loader) : /*#__PURE__*/React.createElement(React.Fragment, null, loader, " ", searchIcon);
  var leadingIcon = showSearchIconLeft ? /*#__PURE__*/React.createElement(React.Fragment, null, searchIcon) : null;
  var handleSearch = function handleSearch(e) {
    var _e$target;
    setSuggest(true);
    onChange(e === null || e === void 0 ? void 0 : (_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.value);
  };
  var handleFocus = function handleFocus() {
    setFocus(true);
    setCursor({});
    onFocus();
  };
  var handleBlur = function handleBlur() {
    setFocus(false);
    onBlur();
  };
  var getSearchResults = function getSearchResults() {
    var showResults = isChina() ? (value === null || value === void 0 ? void 0 : value.length) >= 2 : (value === null || value === void 0 ? void 0 : value.length) >= 3;
    var showHistory = (value === null || value === void 0 ? void 0 : value.length) === 0;
    return showResults || showHistory ? searchResults : null;
  };
  var scopeDDL = enableScope && !hideScopeDropdown ? /*#__PURE__*/React.createElement(FormItem, {
    className: styles['search-bar-scope']
  }, /*#__PURE__*/React.createElement(FormInputDropdown, {
    onOptionSelect: function onOptionSelect(option) {
      var _option$value;
      return setScope((_option$value = option.value) === null || _option$value === void 0 ? void 0 : _option$value.replace('_duplicate', ''));
    },
    className: "".concat(styles['search-bar-scope-list'], " ").concat(dense ? styles['search-bar-input-small-height'] : null),
    enableSearch: false,
    selected: scope,
    options: finalScopeList,
    key: "search-bar-scope"
  })) : null;
  var searchBar = /*#__PURE__*/React.createElement(FormItem, {
    key: "search-bar-form-".concat(id)
  }, /*#__PURE__*/React.createElement(FormInputText, {
    className: "".concat(styles['search-bar-input'], " ").concat(enableScope && !hideScopeDropdown ? styles['search-bar-input-flat-corners'] : null, " ").concat(enableScope && dense ? styles['search-bar-input-small-height'] : null),
    autoFocus: autoFocus,
    autoComplete: "off",
    inputMode: "text",
    value: value,
    inputRef: searchBarRef,
    onChange: handleSearch,
    leading: leadingIcon,
    trailing: trailingIcon,
    placeholder: placeholder,
    onFocus: handleFocus,
    onBlur: handleBlur,
    key: "search-bar-input-".concat(id)
  }));
  var results = /*#__PURE__*/React.createElement(SearchResultsList, {
    cursor: cursor,
    setCursor: setCursor,
    searchResults: getSearchResults(),
    isFocus: isFocus,
    setIgHover: setIgHover,
    ignoreHover: ignoreHover,
    onSelect: triggerC360Search,
    onPressEnter: triggerC360Search,
    setSuggest: setSuggest,
    embedded: embedded,
    id: id,
    trailingComponent: trailingComponent,
    CustomDDLOption: CustomDDLOption,
    isHistory: isHistory
  });
  return /*#__PURE__*/React.createElement("div", {
    key: "search-bar-parent-".concat(id),
    className: "".concat(className, " ").concat(!embedded ? styles['search-bar'] : styles['search-bar--header']),
    onMouseMove: function onMouseMove() {
      return setIgHover(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['search-bar-group']
  }, scopeDDL, /*#__PURE__*/React.createElement("div", {
    className: styles['search-sub-section']
  }, searchBar, suggest && isFocus && results)));
};
SearchBar.defaultProps = {
  value: null,
  placeholder: 'Search',
  recent: 'Recent',
  embedded: false,
  searchCount: 5,
  className: '',
  listData: null,
  onSelect: null,
  type: 'user',
  accountId: null,
  historyData: null,
  onChange: null,
  autoFocus: false,
  trailingComponent: null,
  filters: {},
  CustomDDLOption: null,
  scopeList: [],
  enableScope: false,
  locale: null,
  showSearchIconLeft: false,
  onBlur: function onBlur() {},
  onFocus: function onFocus() {},
  contactSearch: false,
  dense: false,
  hideScopeDropdown: false,
  customSearchFunc: undefined,
  customTransformFunc: undefined,
  debounceTime: 500,
  C360SearchTestConsumerApp: ''
};
var DDL = shape({
  label: string,
  value: string
});
var searchResultsPropType = arrayOf(shape({
  type: string,
  data: arrayOf(DDL)
}));
var filterPropType = shape({
  accountTypes: arrayOf(string)
});
var scopePropType = shape({
  label: string,
  value: string
});
SearchBar.propTypes = {
  // general
  adfsToken: string.isRequired,
  aosUrl: string.isRequired,
  locale: string,
  appName: string.isRequired,
  id: string.isRequired,
  embedded: bool,
  className: string,
  autoFocus: bool,
  // search input
  value: string,
  placeholder: string,
  onChange: func,
  filters: filterPropType,
  scopeList: arrayOf(scopePropType),
  enableScope: bool,
  showSearchIconLeft: bool,
  onBlur: func,
  onFocus: func,
  contactSearch: bool,
  dense: bool,
  hideScopeDropdown: bool,
  debounceTime: number,
  // results list
  recent: string,
  searchCount: number,
  type: string,
  listData: searchResultsPropType,
  onSelect: func,
  trailingComponent: func,
  CustomDDLOption: element,
  customSearchFunc: func,
  customTransformFunc: func,
  // history
  accountId: string,
  historyData: oneOfType([searchResultsPropType, bool]),
  // C360 test header
  C360SearchTestConsumerApp: string
};

export { SearchBar as default };
